import { Component, define } from '@theme/component';

/**
 * A row of slides that scrolls sideways: the photographs beside "The right
 * choice" and the reviews.
 *
 * The scrolling is the browser's own — the track is an overflow container
 * with scroll snapping — so a swipe, a trackpad and the keyboard all work
 * before this file loads, and with it absent. What this adds is the chrome
 * around it:
 *
 *   - `ref="prev"` / `ref="next"` step one slide, disabled at either end;
 *   - `ref="dot"` jumps to its slide, and the current one is marked
 *     `aria-current`, which is also what draws the old site's progress line;
 *   - `data-autoplay="6000"` advances on its own, wrapping at the end, until
 *     the visitor touches any control. Hovering or focusing the slider pauses
 *     it, and with reduced motion asked for it never starts.
 */
class SliderComponent extends Component {
  static requiredRefs = ['track'];

  #index = 0;
  #timer = 0;
  #stopped = false;

  connected() {
    const { prev, next } = this.refs;

    if (prev) this.on(prev, 'click', () => this.#step(-1));
    if (next) this.on(next, 'click', () => this.#step(1));

    for (const [index, dot] of this.#dots.entries()) {
      this.on(dot, 'click', () => {
        this.#stop();
        this.goTo(index);
      });
    }

    this.on(this.refs.track, 'scrollend', this.#sync);
    // Browsers without scrollend still report the position, just later.
    this.on(this.refs.track, 'scroll', this.#onScroll, { passive: true });

    this.on(this, 'pointerenter', this.#pause);
    this.on(this, 'pointerleave', this.#resume);
    this.on(this, 'focusin', this.#pause);
    this.on(this, 'focusout', this.#resume);
    this.on(this.refs.track, 'pointerdown', () => this.#stop());

    this.#render();
    this.#resume();
  }

  disconnected() {
    clearTimeout(this.#timer);
  }

  /** The slides shown; a page can hide some, such as the product page's
      photos of a choice that is not picked. */
  get #slides() {
    return /** @type {HTMLElement[]} */ ([this.refs.slide ?? []].flat()).filter((slide) => !slide.hidden);
  }

  get #dots() {
    return /** @type {HTMLElement[]} */ ([this.refs.dot ?? []].flat());
  }

  /** How many slides are fully in view, so the last step stops at the end. */
  get #lastIndex() {
    const { track } = this.refs;
    const slides = this.#slides;
    const end = track.scrollWidth - track.clientWidth;

    for (const [index, slide] of slides.entries()) {
      if (slide.offsetLeft - slides[0].offsetLeft >= end - 1) return index;
    }

    return Math.max(slides.length - 1, 0);
  }

  /** @param {number} index */
  goTo(index) {
    const slides = this.#slides;
    if (slides.length === 0) return;

    this.#index = Math.min(Math.max(index, 0), this.#lastIndex);
    const left = slides[this.#index].offsetLeft - slides[0].offsetLeft;

    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.refs.track.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
    this.#render();
  }

  /** Catch up after slides were shown, hidden or reordered, staying where
      the track is. */
  refresh() {
    this.#sync();
    this.#index = Math.min(this.#index, this.#lastIndex);
    this.#render();
  }

  /** @param {number} delta */
  #step(delta) {
    this.#stop();
    this.goTo(this.#index + delta);
  }

  #scrollTimer = 0;

  #onScroll = () => {
    clearTimeout(this.#scrollTimer);
    this.#scrollTimer = setTimeout(this.#sync, 120);
  };

  /** Work out which slide the visitor scrolled to, by hand or by snap. */
  #sync = () => {
    const slides = this.#slides;
    if (slides.length === 0) return;

    const position = this.refs.track.scrollLeft;
    let closest = 0;

    for (const [index, slide] of slides.entries()) {
      const offset = slide.offsetLeft - slides[0].offsetLeft;
      if (Math.abs(offset - position) < Math.abs(slides[closest].offsetLeft - slides[0].offsetLeft - position)) {
        closest = index;
      }
    }

    if (closest !== this.#index) {
      this.#index = closest;
      this.#render();
    }
  };

  #render() {
    const { prev, next } = this.refs;

    if (prev instanceof HTMLButtonElement) prev.disabled = this.#index <= 0;
    if (next instanceof HTMLButtonElement) next.disabled = this.#index >= this.#lastIndex;

    // One dot per slide shown.
    const count = this.#slides.length;
    for (const [index, dot] of this.#dots.entries()) {
      dot.hidden = index >= count;
      if (index === this.#index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    }
  }

  get #delay() {
    return Number(this.dataset.autoplay) || 0;
  }

  #pause = () => {
    clearTimeout(this.#timer);
    this.toggleAttribute('data-paused', true);
  };

  #resume = () => {
    if (this.#stopped || !this.#delay || this.#slides.length < 2) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Focus moving between two controls of this slider is not leaving it.
    if (this.matches(':focus-within, :hover')) return;

    this.removeAttribute('data-paused');
    clearTimeout(this.#timer);
    this.#timer = setTimeout(this.#advance, this.#delay);
  };

  #advance = () => {
    const next = this.#index >= this.#lastIndex ? 0 : this.#index + 1;
    this.goTo(next);
    this.#resume();
  };

  /** Any deliberate move by the visitor ends autoplay for good. */
  #stop() {
    this.#stopped = true;
    clearTimeout(this.#timer);
    this.setAttribute('data-stopped', '');
  }
}

define('slider-component', SliderComponent);
